import React, { useState } from 'react';
import {
  Button,
  Select,
  Divider,
  Tooltip,
  Dropdown,
  ColorPicker,
  Popover,
} from 'antd';
import EmojiPicker from 'emoji-picker-react';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  UndoOutlined,
  RedoOutlined,
  LinkOutlined,
  BgColorsOutlined,
  FontColorsOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  TableOutlined,
  SmileOutlined,
  UploadOutlined,
  ColumnWidthOutlined,
  CalendarOutlined,
  TagOutlined,
  PlusOutlined,
  DownOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  MinusOutlined,
  CheckSquareOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $insertNodes,
  $createTextNode,
} from 'lexical';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import {
  MentionNode,
  LayoutNode,
  UploadNode,
  LayoutColumnNode,
  DateNode,
  StatusNode,
  CodeBlockNode,
  PanelNode,
  DividerNode,
  CheckboxNode,
} from './CustomNode';
import { $patchStyleText } from '@lexical/selection';

export default function ToolbarPlugin({ onUpload }) {
  const [editor] = useLexicalComposerContext();
  const [statusText, setStatusText] = useState('');

  /* TEXT FORMAT */

  const format = (type) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
  };

  /* ALIGN */

  const align = (type) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, type);
  };

  /* FONT SIZE */

  const applyFontSize = (size) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach((node) => {
          node.setStyle(`font-size:${size}px`);
        });
      }
    });
  };

  /* LINK */

  const insertLink = () => {
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) return;

      let text = selection.getTextContent();

      if (!text.startsWith('http')) {
        text = 'https://' + text;
      }

      editor.dispatchCommand(TOGGLE_LINK_COMMAND, text);
    });
  };

  /* TABLE */

  const insertTable = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: 4,
      columns: 4,
    });
  };

  /* UPLOAD */

  const insertUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      let inlinePosition = null;
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        const anchor = selection.anchor;
        inlinePosition = {
          block_id: anchor.key,
          start_offset: anchor.offset,
          end_offset: anchor.offset,
        };
      });

      await onUpload(file, inlinePosition, file.type);

      // const fileUrl = res?.attachment_url;
      // const isImage = file.type.startsWith('image/');

      // editor.update(() => {
      //   const selection = $getSelection();
      //   if (!$isRangeSelection(selection)) return;

      //   const node = new UploadNode(file.name, fileUrl, isImage, res?.id);

      //   selection.insertNodes([node]);
      // });
    };

    input.click();
  };

  /* LAYOUT */

  const insertLayout = () => {
    editor.update(() => {
      const layout = new LayoutNode();

      const col1 = new LayoutColumnNode();
      const col2 = new LayoutColumnNode();

      col1.append($createParagraphNode());
      col2.append($createParagraphNode());

      layout.append(col1, col2);

      $insertNodes([layout]);
    });
  };

  /* DATE */

  const insertDate = () => {
    editor.update(() => {
      const node = new DateNode(new Date().toLocaleDateString());

      const paragraph = $createParagraphNode();

      paragraph.append(node);

      $insertNodes([paragraph]);
    });
  };

  /* STATUS */

  const insertStatus = (text, color) => {
    editor.update(() => {
      const statusNode = new StatusNode(text, color);
      const paragraph = $createParagraphNode();
      paragraph.append(statusNode);
      $insertNodes([paragraph]);
    });
  };

  /* MENTION */

  const insertMention = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      selection.insertText('@');
    });
  };

  /* COLORS */

  const applyTextColor = (color) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, {
          color: color.toHexString(),
        });
      }
    });
  };

  const applyBgColor = (color) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, {
          'background-color': color.toHexString(),
        });
      }
    });
  };

  //EMOJI
  const addEmoji = (emojiData) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selection.insertText(emojiData.emoji);
      }
    });
  };

  /* CODE BLOCK */
  const insertCodeBlock = () => {
    editor.update(() => {
      const code = new CodeBlockNode();
      const paragraph = $createParagraphNode();
      code.append(paragraph);
      $insertNodes([code]);
    });
  };

  // panel
  const insertPanel = (type = 'info') => {
    editor.update(() => {
      const panel = new PanelNode(type);
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode());
      panel.append(paragraph);
      $insertNodes([panel]);
      // paragraph.select();
    });
  };

  /* DIVIDER */
  const insertDivider = () => {
    editor.update(() => {
      const divider = new DividerNode();
      $insertNodes([divider]);
    });
  };

  /* CHECKBOX */
  const insertCheckbox = () => {
    editor.update(() => {
      const checkbox = new CheckboxNode(false);
      const paragraph = $createParagraphNode();
      checkbox.append(paragraph);
      $insertNodes([checkbox]);
    });
  };

  const deleteSelected = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const node = selection.anchor.getNode();
        node.remove();
      }
    });
  };

  const textMenu = {
    items: [
      {
        key: 'italic',
        label: 'Italic',
        icon: <ItalicOutlined />,
        onClick: () => format('italic'),
      },
      {
        key: 'underline',
        label: 'Underline',
        icon: <UnderlineOutlined />,
        onClick: () => format('underline'),
      },
      {
        key: 'strike',
        label: 'Strikethrough',
        icon: <StrikethroughOutlined />,
        onClick: () => format('strikethrough'),
      },
    ],
  };

  const listMenu = {
    items: [
      {
        key: 'bullet',
        label: 'Bullet List',
        icon: <UnorderedListOutlined />,
        onClick: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND),
      },
      {
        key: 'number',
        label: 'Numbered List',
        icon: <OrderedListOutlined />,
        onClick: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND),
      },
    ],
  };

  const alignMenu = {
    items: [
      {
        key: 'left',
        label: 'Align Left',
        icon: <AlignLeftOutlined />,
        onClick: () => align('left'),
      },
      {
        key: 'center',
        label: 'Align Center',
        icon: <AlignCenterOutlined />,
        onClick: () => align('center'),
      },
      {
        key: 'right',
        label: 'Align Right',
        icon: <AlignRightOutlined />,
        onClick: () => align('right'),
      },
    ],
  };

  const PlusMenuOverlay = () => {
    const Item = ({ icon, title, desc, onClick }) => (
      <div
        className="plus-item"
        onClick={() => {
          onClick();
        }}
      >
        <div className="plus-icon">{icon}</div>
        <div>
          <div className="plus-title">{title}</div>
          <div className="plus-desc">{desc}</div>
        </div>
      </div>
    );

    return (
      <div className="plus-menu">
        {/* BASIC */}
        <Item
          icon={<LinkOutlined />}
          title="Link"
          desc="Insert a hyperlink"
          onClick={insertLink}
        />

        <Item
          icon={<TableOutlined />}
          title="Table"
          desc="Insert a 4x4 table"
          onClick={insertTable}
        />

        <Item
          icon={<ColumnWidthOutlined />}
          title="Layout"
          desc="Create multi-column layout"
          onClick={insertLayout}
        />

        <Item
          icon={<UploadOutlined />}
          title="Upload"
          desc="Insert image or file"
          onClick={insertUpload}
        />

        <Item
          icon={<CalendarOutlined />}
          title="Date"
          desc="Insert today's date"
          onClick={insertDate}
        />

        <Item
          icon={<TagOutlined />}
          title="Mention"
          desc="Mention a user"
          onClick={insertMention}
        />

        {/* ADVANCED BLOCKS */}
        <Item
          icon={<CodeOutlined />}
          title="Code Block"
          desc="Display code with syntax highlighting"
          onClick={insertCodeBlock}
        />

        <Item
          icon={<InfoCircleOutlined />}
          title="Info Panel"
          desc="Highlight important information"
          onClick={() => insertPanel('info')}
        />

        <Item
          icon={<CheckCircleOutlined />}
          title="Success Panel"
          desc="Show success message"
          onClick={() => insertPanel('success')}
        />

        <Item
          icon={<WarningOutlined />}
          title="Warning Panel"
          desc="Show warning message"
          onClick={() => insertPanel('warning')}
        />

        <Item
          icon={<CloseCircleOutlined />}
          title="Error Panel"
          desc="Show error message"
          onClick={() => insertPanel('error')}
        />

        <Item
          icon={<MinusOutlined />}
          title="Divider"
          desc="Insert horizontal divider"
          onClick={insertDivider}
        />

        <Item
          icon={<CheckSquareOutlined />}
          title="Checkbox"
          desc="Insert checklist item"
          onClick={insertCheckbox}
        />
      </div>
    );
  };

  return (
    <div className="editor-toolbar">
      {/* FONT SIZE */}
      <Tooltip title="Font Size">
        <Select
          defaultValue="14"
          style={{ width: 200 }}
          onChange={applyFontSize}
          options={[
            { value: '12', label: '12' },
            { value: '14', label: '14' },
            { value: '16', label: '16' },
            { value: '18', label: '18' },
            { value: '24', label: '24' },
            { value: '30', label: '30' },
          ]}
        />
      </Tooltip>

      <Divider type="vertical" />

      {/* TEXT */}

      <Tooltip title="bold">
        <Button icon={<BoldOutlined />} onClick={() => format('bold')} />
      </Tooltip>

      <Dropdown menu={textMenu}>
        <Tooltip title="Format Text">
          <Button icon={<DownOutlined />} />
        </Tooltip>
      </Dropdown>

      {/* LIST */}
      <Dropdown menu={listMenu}>
        <Tooltip title="Lists">
          <Button icon={<UnorderedListOutlined />} />
        </Tooltip>
      </Dropdown>

      {/* ALIGN */}
      <Dropdown menu={alignMenu}>
        <Tooltip title="Align">
          <Button icon={<AlignLeftOutlined />} />
        </Tooltip>
      </Dropdown>

      <Divider type="vertical" />

      {/* LINK */}
      <Tooltip title="Insert Link">
        <Button icon={<LinkOutlined />} onClick={insertLink} />
      </Tooltip>

      {/* TABLE */}
      <Tooltip title="Insert Table">
        <Button icon={<TableOutlined />} onClick={insertTable} />
      </Tooltip>

      {/* LAYOUT */}
      <Tooltip title="Insert Layout">
        <Button icon={<ColumnWidthOutlined />} onClick={insertLayout} />
      </Tooltip>

      {/* UPLOAD */}
      <Tooltip title="Upload File/Image">
        <Button icon={<UploadOutlined />} onClick={insertUpload} />
      </Tooltip>

      <Divider type="vertical" />

      {/* COLORS */}
      <ColorPicker onChange={applyTextColor}>
        <Tooltip title="Text Color">
          <Button icon={<FontColorsOutlined />} />
        </Tooltip>
      </ColorPicker>

      <ColorPicker onChange={applyBgColor}>
        <Tooltip title="Background Color">
          <Button icon={<BgColorsOutlined />} />
        </Tooltip>
      </ColorPicker>

      {/* STATUS */}
      <Popover
        trigger="click"
        getPopupContainer={() => document.body}
        overlayStyle={{ zIndex: 1400 }}
        content={
          <div style={{ width: 200 }}>
            <input
              placeholder="Set a status"
              style={{ width: '100%', marginBottom: 10, padding: 6 }}
              id="statusInput"
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                '#DFE1E6',
                '#4C9AFF',
                '#57D9A3',
                '#FFAB00',
                '#FF5630',
                '#B39DDB',
              ].map((color) => (
                <div
                  key={color}
                  onClick={() => {
                    const text =
                      document.getElementById('statusInput').value || 'Status';
                    insertStatus(text, color);
                  }}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: color,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>
        }
      >
        <Tooltip title="Add status">
          <Button icon={<TagOutlined />} />
        </Tooltip>
      </Popover>

      {/* EMOJI */}
      <Popover content={<EmojiPicker onEmojiClick={addEmoji} />}>
        <Tooltip title="Emoji">
          <Button icon={<SmileOutlined />} />
        </Tooltip>
      </Popover>

      {/* delete */}
      <Button icon={<DeleteOutlined />} onClick={deleteSelected} />

      {/* PLUS MENU */}
      <Dropdown
        dropdownRender={() => <PlusMenuOverlay />}
        trigger={['click']}
        placement="bottomLeft"
      >
        <Tooltip title="More Options">
          <Button icon={<PlusOutlined />} />
        </Tooltip>
      </Dropdown>

      <Divider type="vertical" />

      {/* UNDO REDO */}
      <Tooltip title="Undo">
        <Button
          icon={<UndoOutlined />}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND)}
        />
      </Tooltip>

      <Tooltip title="Redo">
        <Button
          icon={<RedoOutlined />}
          onClick={() => editor.dispatchCommand(REDO_COMMAND)}
        />
      </Tooltip>
    </div>
  );
}
